'use client'

import { useTranslations } from 'next-intl'
import { classifyUploadFailure, detectUploadDevice } from './upload-failure'

/**
 * Message for an UpChunk `error` event, in the storefront's locale. Both video
 * uploaders (lesson and event) show it in the toast and the failed-video panel.
 */
export function useUploadFailureMessage(): (upchunkMessage: string | undefined, file: File) => string {
  const t = useTranslations('courses.video')

  return (upchunkMessage, file) => {
    const device = detectUploadDevice({
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints,
    })
    const failure = classifyUploadFailure(upchunkMessage, { fileSize: file.size, device })
    // The raw library text stays in the console for support; the UI only gets copy.
    console.error('[video-upload] upload failed', { upchunkMessage, fileSize: file.size, device, failure })

    switch (failure.kind) {
      case 'file_unreadable':
        if (failure.device === 'ios')
          return failure.overWebKitLimit ? t('errors.iosOverLimit') : t('errors.iosUnreadable')
        if (failure.device === 'mac_safari')
          return failure.overWebKitLimit ? t('errors.safariOverLimit') : t('errors.safariUnreadable')
        return t('errors.fileUnreadable')
      case 'endpoint_unavailable':
        return t('errors.uploadUnavailable')
      case 'server_rejected':
        return t('errors.uploadRejected', { status: failure.status })
      case 'unknown':
        return t('errors.uploadFailed')
    }
  }
}
